'use client';

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

interface Project {
  id: number;
  title_en?: string;
  title_ar?: string;
  title_de?: string;
  title_es?: string;
  title_fr?: string;
  title_it?: string;
  title_nl?: string;
  title_pt?: string;
  title_ru?: string;
  title_sv?: string;
  description_en?: string;
  description_ar?: string;
  image_en?: string;
  image_ar?: string;
  url_en?: string;
  url_ar?: string;
  [key: string]: any;
}

interface FeaturedWorkProps {
  posts: Project[];
  isLoading: boolean;
  error: boolean;
  locale: string;
  getLocalizedField: (item: any, field: string) => any;
  title?: string;
}

const SkeletonLoader = () => (
  <div className="flex flex-col gap-12">
    {[1, 2, 3].map((row) => (
      <div key={row} className="flex flex-col md:flex-row gap-6">
        {[1, 2].map((item) => (
          <div key={item} className="flex-1 flex flex-col gap-4">
            <div className="aspect-[4/3] bg-gray-300 animate-pulse rounded" />
            <div className="h-6 bg-gray-300 animate-pulse rounded w-3/4" />
          </div>
        ))}
      </div>
    ))}
  </div>
);

export default function FeaturedWork({ 
  posts, 
  isLoading, 
  error, 
  locale, 
  getLocalizedField,
  title = "Latest News"
}: FeaturedWorkProps) {
  const workContainerRef = useRef<HTMLDivElement>(null);
  const lenisRef = useRef<Lenis | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const scrollTriggerRef = useRef<ScrollTrigger | null>(null);
  const animationTimelineRef = useRef<gsap.core.Timeline | null>(null);
  
  // State for lazy loading
  const [visiblePosts, setVisiblePosts] = useState<Project[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Memoized posts array to prevent unnecessary re-renders
  const allPosts = useMemo(() => posts || [], [posts]);

  // Initialize visible posts
  useEffect(() => {
    if (allPosts.length > 0 && visiblePosts.length === 0) {
      // Show first 2 posts initially
      setVisiblePosts(allPosts.slice(0, 2));
      setHasMore(allPosts.length > 2);
    }
  }, [allPosts, visiblePosts.length]);

  // GSAP and Lenis setup
  useEffect(() => {
    if (isLoading || error || visiblePosts.length === 0) return;

    let mounted = true;
    let animationTimeout: NodeJS.Timeout;

    const initAnimations = () => {
      if (!mounted) return;

      gsap.registerPlugin(ScrollTrigger);

      // Initialize Lenis smooth scroll
      if (!lenisRef.current) {
        lenisRef.current = new Lenis({
          duration: 1.2,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          smoothWheel: true,
          syncTouch: true,
        });

        lenisRef.current.on('scroll', ScrollTrigger.update);

        const tickerCallback = (time: number) => {
          lenisRef.current?.raf(time * 1000);
        };

        gsap.ticker.add(tickerCallback);
        gsap.ticker.lagSmoothing(0);
      }

      // Animate initial posts
      animationTimeout = setTimeout(() => {
        if (!mounted) return;

        const initialItems = document.querySelectorAll('.featured-work-item:not(.animated)');
        
        if (initialItems.length > 0) {
          animationTimelineRef.current = gsap.timeline();
          
          initialItems.forEach((item, index) => {
            item.classList.add('animated');
            animationTimelineRef.current?.fromTo(item,
              {
                y: 80,
                opacity: 0,
                rotation: index % 2 === 0 ? -15 : 15,
              },
              {
                y: 0,
                opacity: 1,
                rotation: 0,
                duration: 0.8,
                ease: "power3.out",
                delay: index * 0.15,
              },
              index * 0.15
            );
          });
        }
      }, 100);
    };

    initAnimations();

    return () => {
      mounted = false;
      clearTimeout(animationTimeout);
      
      // Only destroy on component unmount
      if (workContainerRef.current === null) {
        if (animationTimelineRef.current) {
          animationTimelineRef.current.kill();
        }
        if (scrollTriggerRef.current) {
          scrollTriggerRef.current.kill();
        }
        if (lenisRef.current) {
          lenisRef.current.destroy();
          lenisRef.current = null;
        }
        ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      }
    };
  }, [isLoading, error, visiblePosts.length]);

  // Scroll loading with debounce
  const loadMorePosts = useCallback(() => {
    if (isLoadingMore || !hasMore || allPosts.length <= visiblePosts.length) return;

    setIsLoadingMore(true);
    
    // Simulate network delay for better UX
    setTimeout(() => {
      const currentLength = visiblePosts.length;
      const nextPosts = allPosts.slice(currentLength, currentLength + 2);
      
      if (nextPosts.length > 0) {
        setVisiblePosts(prev => [...prev, ...nextPosts]);
        setHasMore(allPosts.length > currentLength + nextPosts.length);
        
        // Animate new posts after DOM update
        requestAnimationFrame(() => {
          setTimeout(() => {
            const allItems = document.querySelectorAll('.featured-work-item:not(.animated)');
            
            if (allItems.length > 0) {
              const animationTimeline = gsap.timeline();
              
              allItems.forEach((item, index) => {
                item.classList.add('animated');
                animationTimeline.fromTo(item,
                  {
                    y: 80,
                    opacity: 0,
                    rotation: (currentLength + index) % 2 === 0 ? -15 : 15,
                  },
                  {
                    y: 0,
                    opacity: 1,
                    rotation: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    delay: index * 0.15,
                  },
                  index * 0.15
                );
              });
              
              animationTimelineRef.current = animationTimeline;
            }
            
            setIsLoadingMore(false);
          }, 50);
        });
      } else {
        setIsLoadingMore(false);
      }
    }, 300);
  }, [visiblePosts.length, allPosts, isLoadingMore, hasMore]);

  // Intersection Observer for scroll loading
  useEffect(() => {
    if (!hasMore || isLoadingMore || visiblePosts.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingMore && hasMore) {
          loadMorePosts();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    const currentLoadMoreRef = loadMoreRef.current;
    if (currentLoadMoreRef && hasMore) {
      observer.observe(currentLoadMoreRef);
    }

    return () => {
      if (currentLoadMoreRef) {
        observer.unobserve(currentLoadMoreRef);
      }
    };
  }, [hasMore, isLoadingMore, loadMorePosts, visiblePosts.length]);

  // Memoized render function
  const renderProjects = useCallback(() => {
    const rows = [];
    
    for (let i = 0; i < visiblePosts.length; i += 2) {
      const leftProject = visiblePosts[i];
      const rightProject = visiblePosts[i + 1];
      const rowIndex = Math.floor(i / 2);

      rows.push(
        <div key={`row-${rowIndex}`} className="featured-row flex flex-col md:flex-row gap-6">
          <WorkItem 
            project={leftProject} 
            locale={locale} 
            getLocalizedField={getLocalizedField} 
            index={i}
          />
          {rightProject && (
            <WorkItem 
              project={rightProject} 
              locale={locale} 
              getLocalizedField={getLocalizedField} 
              index={i + 1}
            />
          )}
        </div>
      );
    }

    return rows;
  }, [visiblePosts, locale, getLocalizedField]);

  return (
    <div className="w-full px-3 lg:px-5">
      <h2 className="text-2xl sm:text-3xl font-extrabold py-10 uppercase">{title}</h2>
      
      <section
        ref={workContainerRef}
        className="featured-work-container relative w-full flex flex-col gap-12 overflow-hidden pb-10"
      >
        {isLoading ? (
          <SkeletonLoader />
        ) : error ? (
          <SkeletonLoader />
        ) : (
          <>
            {renderProjects()}
            
            {/* Load more trigger element */}
            {hasMore && (
              <div 
                ref={loadMoreRef} 
                className="h-20 flex items-center justify-center"
              >
                {isLoadingMore ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                   
                  </div>
                ) : (
                  <div className="h-10" /> // Empty space as trigger
                )}
              </div>
            )}
            
            {/* End of content message */}
            {!hasMore && visiblePosts.length > 0 && (
              <div className="text-center py-8 text-gray-400 text-sm">
                
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

interface WorkItemProps {
  project: Project;
  locale: string;
  getLocalizedField: (item: any, field: string) => any;
  index: number;
}

function WorkItem({ project, locale, getLocalizedField, index }: WorkItemProps) {
  const [imageError, setImageError] = useState(false);
  const imageUrl = getLocalizedField(project, 'image');
  const fullImageUrl = imageUrl ? `${process.env.NEXT_PUBLIC_IMAGE}/${imageUrl}` : '';

  // Memoize the localized fields
  const localizedTitle = useMemo(
    () => getLocalizedField(project, 'title'),
    [project, getLocalizedField]
  );

  const localizedDescription = useMemo(
    () => getLocalizedField(project, 'description'),
    [project, getLocalizedField]
  );

  const localizedUrl = useMemo(
    () => getLocalizedField(project, 'url'),
    [project, getLocalizedField]
  );

  const localizedAlt = useMemo(
    () => getLocalizedField(project, 'tag') || localizedTitle,
    [project, getLocalizedField, localizedTitle]
  );

  return (
    <div className="featured-work-item flex-1 flex flex-col gap-4 lg:px-6">
      <Link
        href={`/${locale}/blog/${localizedUrl}`}
        className="flex flex-col gap-4 no-underline text-inherit w-full h-full group cursor-pointer"
        prefetch={false}
      >
        <div className="work-item-img aspect-[4/3] h-60 md:h-80 lg:h-96 overflow-hidden relative rounded-lg bg-gray-100">
          <div 
            className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-20 transition-all duration-300 z-10"
          />
          
          {!imageError && fullImageUrl ? (
            <Image
              src={fullImageUrl}
              alt={localizedAlt}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
              className="object-cover transition-transform duration-700 group-hover:scale-105"
              loading={index < 4 ? "eager" : "lazy"}
              priority={index < 2}
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
              <span className="text-gray-400"></span>
            </div>
          )}
        </div>
            
        <div className="py-1 flex flex-col gap-2">
          <h2 className="font-semibold text-lg uppercase line-clamp-2">
            {localizedTitle}
          </h2>
          <p className="text-sm text-gray-700 line-clamp-3">
            {localizedDescription}
          </p>
        </div>
      </Link>
    </div>
  );
}